"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

interface HeroSlide {
  src: string;
  alt: string;
  badge: string;
  heading: string;
  headingHighlight: string;
  subtext: string;
  cta: string;
  ctaActive: boolean;
  sortOrder: number;
  active: boolean;
}

export default function Hero() {
  const { locale } = useLocale();

  const defaultHeroImages: HeroSlide[] = [
    {
      src: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1920&q=80",
      alt: t(locale, "hero.alt.0"),
      badge: t(locale, "hero.badge.0"),
      heading: t(locale, "hero.heading.0"),
      headingHighlight: t(locale, "hero.highlight.0"),
      subtext: t(locale, "hero.subtext.0"),
      cta: t(locale, "hero.cta.0"),
      ctaActive: true,
      sortOrder: 0,
      active: true,
    },
    {
      src: "https://images.unsplash.com/photo-1559737558-2f5a35f4523b?w=1920&q=80",
      alt: t(locale, "hero.alt.1"),
      badge: t(locale, "hero.badge.1"),
      heading: t(locale, "hero.heading.1"),
      headingHighlight: t(locale, "hero.highlight.1"),
      subtext: t(locale, "hero.subtext.1"),
      cta: t(locale, "hero.cta.1"),
      ctaActive: true,
      sortOrder: 1,
      active: true,
    },
    {
      src: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1920&q=80",
      alt: t(locale, "hero.alt.2"),
      badge: t(locale, "hero.badge.2"),
      heading: t(locale, "hero.heading.2"),
      headingHighlight: t(locale, "hero.highlight.2"),
      subtext: t(locale, "hero.subtext.2"),
      cta: t(locale, "hero.cta.2"),
      ctaActive: true,
      sortOrder: 2,
      active: true,
    },
    {
      src: "https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?w=1920&q=80",
      alt: t(locale, "hero.alt.3"),
      badge: t(locale, "hero.badge.3"),
      heading: t(locale, "hero.heading.3"),
      headingHighlight: t(locale, "hero.highlight.3"),
      subtext: t(locale, "hero.subtext.3"),
      cta: t(locale, "hero.cta.3"),
      ctaActive: true,
      sortOrder: 3,
      active: true,
    },
    {
      src: "https://images.unsplash.com/photo-1527689368864-3a821dbccc34?w=1920&q=80",
      alt: t(locale, "hero.alt.4"),
      badge: t(locale, "hero.badge.4"),
      heading: t(locale, "hero.heading.4"),
      headingHighlight: t(locale, "hero.highlight.4"),
      subtext: t(locale, "hero.subtext.4"),
      cta: t(locale, "hero.cta.4"),
      ctaActive: true,
      sortOrder: 4,
      active: true,
    },
  ];

  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(defaultHeroImages);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  useEffect(() => {
    async function fetchHeroSlides() {
      try {
        const res = await fetch("/api/admin/hero");
        if (res.ok) {
          const slides = await res.json();
          const activeSlides = slides.filter((s: HeroSlide) => s.active).sort((a: HeroSlide, b: HeroSlide) => a.sortOrder - b.sortOrder);
          if (activeSlides.length > 0) {
            setHeroSlides(activeSlides);
          }
        }
      } catch (error) {
        console.error("Failed to fetch hero slides:", error);
      }
      setLoading(false);
    }
    fetchHeroSlides();
  }, []);

  useEffect(() => {
    if (loading) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length, loading]);

  return (
    <section
      className="relative min-h-screen flex items-end pb-20 md:pb-28 overflow-hidden"
      id="home"
    >
      <div className="absolute inset-0 z-0">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            {brokenImages.has(index) ? (
              <div className="absolute inset-0 bg-brand-900" />
            ) : (
            <Image
              src={slide.src}
              alt={slide.alt || slide.heading}
              fill
              className="object-cover"
              sizes="100vw"
              priority={index === 0}
              loading={index === 0 ? undefined : "lazy"}
              onError={() => setBrokenImages((prev) => new Set(prev).add(index))}
            />
            )}
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950/90 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-900/50 via-black/20 to-transparent" />
      </div>

      <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? "bg-gold-500"
                : "bg-white/30 hover:bg-white/50"
            }`}
            aria-label={t(locale, "hero.slideLabel", { n: String(index + 1) })}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl relative min-h-[400px] md:min-h-[360px]">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 flex flex-col ${
                index === currentSlide
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4 pointer-events-none"
              }`}
            >
              {slide.badge && (
                <p className="text-gold-400 text-sm md:text-base font-medium tracking-wider uppercase mb-6">
                  {slide.badge}
                </p>
              )}
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-display text-white leading-[1.1] tracking-tight mb-8">
                {slide.heading}
                <span className="text-gold-400">
                  {" "}
                  {slide.headingHighlight}
                </span>
              </h1>
              {slide.subtext && (
                <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed mb-4 md:mb-8">
                  {slide.subtext}
                </p>
              )}
              <div className="flex-1" />
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 md:gap-4 mb-6">
                {slide.ctaActive && (
                <Link
                  href="/products"
                  className="group w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-4 rounded-full bg-gold-500 text-slate-900 font-semibold hover:bg-gold-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-gold-500/20 hover:scale-105 active:scale-95 duration-200"
                >
                  {slide.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                )}
                <Link
                  href="/about"
                  className="group w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-4 rounded-full border border-white/30 bg-white/10 text-white font-medium hover:bg-white/20 hover:border-white/50 transition-all flex items-center justify-center gap-2 backdrop-blur-sm"
                >
                  {t(locale, "hero.aboutUs")}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
